import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import Heading from "../../../shared/ui/Typography/Heading";
import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";

import {
  fetchClientMatches,
  requestClientMatchesRecompute,
} from "../api/matches";
import { messageFromError } from "../../../shared/api/drfErrors";
import { buildPreferencesSummaryRows } from "../lib/preferencesSummary";
import ClientMatchCard from "./ClientMatchCard";

// ClientMatchesSection — property matching cards
// (CRM-CLIENT-CONTACTS-MATCHING-A1).
//
// State matrix:
//   1. no preferences → CTA to criteria
//   2. criteria saved / recompute pending → honest inline status
//   3. prefs exist, matches empty → refine criteria
//   4. matches → 2-col / 1-col grid
//   5. recoverable errors; refresh/load-more must not wipe cards
//
// Seller-only clients hide this section (same as preferences).

const PAGE_SIZE = 12;
const POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 1500;

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Усі статуси" },
  { value: "new", label: "Новий" },
  { value: "contacted", label: "Зв\u2019язалися" },
  { value: "viewing", label: "Перегляд" },
  { value: "rejected", label: "Відхилено" },
];

const ORDERING_OPTIONS = [
  { value: "-score", label: "За релевантністю ↓" },
  { value: "score", label: "За релевантністю ↑" },
  { value: "-price", label: "За ціною ↓" },
  { value: "price", label: "За ціною ↑" },
  { value: "-created_at", label: "За датою ↓" },
  { value: "created_at", label: "За датою ↑" },
];

const unwrapPage = (data) => {
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      next: null,
      previous: null,
    };
  }
  if (data && typeof data === "object") {
    return {
      results: Array.isArray(data.results) ? data.results : [],
      count: typeof data.count === "number" ? data.count : 0,
      next: data.next || null,
      previous: data.previous || null,
    };
  }
  return { results: [], count: 0, next: null, previous: null };
};

const hasPreferences = (client) => {
  if (!client || !client.preferences) return false;
  return buildPreferencesSummaryRows(client.preferences).length > 0;
};

function ClientMatchesSection({
  clientId,
  canEdit = true,
  client,
  preferencesRevision = 0,
  onRequestEditPreferences,
}) {
  const clientType = client && client.type;
  const prefsPresent = hasPreferences(client);

  const [matches, setMatches] = useState([]);
  const [count, setCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [ordering, setOrdering] = useState("-score");

  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);

  const [recomputing, setRecomputing] = useState(false);
  const [recomputeError, setRecomputeError] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);

  const requestGenerationRef = useRef(0);
  const activeControllerRef = useRef(null);
  const pollControllerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const baselineCountRef = useRef(null);

  const abortActive = () => {
    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
      activeControllerRef.current = null;
    }
  };

  const abortPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (pollControllerRef.current) {
      pollControllerRef.current.abort();
      pollControllerRef.current = null;
    }
  };

  const buildParams = (pageNum) => {
    const params = {
      page: pageNum,
      page_size: PAGE_SIZE,
      ordering,
    };
    if (statusFilter) {
      params.status = statusFilter;
    }
    return params;
  };

  const loadMatches = async ({
    mode = "initial",
    pageNum = 1,
  } = {}) => {
    if (clientId == null || !prefsPresent) return;

    abortActive();
    const controller = new AbortController();
    activeControllerRef.current = controller;
    const generation = ++requestGenerationRef.current;

    if (mode === "initial" || mode === "filter") {
      setLoadState("loading");
      setLoadError(null);
      setLoadMoreError(null);
      setRefreshError(null);
    } else if (mode === "refresh") {
      setRefreshing(true);
      setRefreshError(null);
    } else if (mode === "load-more") {
      setLoadingMore(true);
      setLoadMoreError(null);
    } else if (mode === "poll") {
      // silent — do not wipe or toggle full loading
    }

    try {
      const data = await fetchClientMatches(
        clientId,
        buildParams(pageNum),
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (generation !== requestGenerationRef.current) return;

      const pageData = unwrapPage(data);

      if (mode === "load-more") {
        setMatches((prev) => {
          const seen = new Set();
          const merged = [];
          for (const row of prev) {
            if (!row || row.id == null || seen.has(row.id)) continue;
            seen.add(row.id);
            merged.push(row);
          }
          for (const row of pageData.results) {
            if (!row || row.id == null || seen.has(row.id)) continue;
            seen.add(row.id);
            merged.push(row);
          }
          return merged;
        });
      } else {
        setMatches(pageData.results);
      }
      setCount(pageData.count);
      setNextPage(pageData.next);
      setPage(pageNum);
      setLoadState("loaded");
      if (mode === "refresh") setRefreshError(null);
      if (mode === "load-more") setLoadMoreError(null);
      return pageData;
    } catch (err) {
      if (controller.signal.aborted) return undefined;
      if (generation !== requestGenerationRef.current) return undefined;
      const message = messageFromError(err);
      if (mode === "refresh" || mode === "poll") {
        setRefreshError(message);
      } else if (mode === "load-more") {
        setLoadMoreError(message);
      } else {
        setLoadError(message);
        setLoadState("error");
      }
      return undefined;
    } finally {
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
      if (generation === requestGenerationRef.current) {
        if (mode === "refresh") setRefreshing(false);
        if (mode === "load-more") setLoadingMore(false);
      }
    }
  };

  const startBoundedPoll = (baselineCount) => {
    abortPoll();
    baselineCountRef.current = baselineCount;
    setPendingMessage("Оновлення підборів…");

    let attempt = 0;

    const runAttempt = async () => {
      attempt += 1;
      const controller = new AbortController();
      pollControllerRef.current = controller;
      const generationAtStart = requestGenerationRef.current;

      try {
        const data = await fetchClientMatches(
          clientId,
          buildParams(1),
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (generationAtStart !== requestGenerationRef.current) return;

        const pageData = unwrapPage(data);
        const baseline = baselineCountRef.current;
        const changed = baseline === null || pageData.count !== baseline;

        setMatches(pageData.results);
        setCount(pageData.count);
        setNextPage(pageData.next);
        setPage(1);
        setLoadState("loaded");

        if (changed) {
          setPendingMessage(null);
          abortPoll();
          return;
        }

        if (attempt >= POLL_ATTEMPTS) {
          setPendingMessage(
            "Підбори можуть ще оновлюватися. Спробуйте «Оновити» трохи згодом.",
          );
          abortPoll();
          return;
        }

        pollTimerRef.current = setTimeout(runAttempt, POLL_INTERVAL_MS);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (generationAtStart !== requestGenerationRef.current) return;
        if (attempt >= POLL_ATTEMPTS) {
          setPendingMessage(
            "Не вдалося підтвердити оновлення підборів. Спробуйте «Оновити».",
          );
          abortPoll();
          return;
        }
        pollTimerRef.current = setTimeout(runAttempt, POLL_INTERVAL_MS);
      } finally {
        if (pollControllerRef.current === controller) {
          pollControllerRef.current = null;
        }
      }
    };

    pollTimerRef.current = setTimeout(runAttempt, POLL_INTERVAL_MS);
  };

  // Initial / filter / client / preferencesRevision load
  useEffect(() => {
    abortPoll();
    setPendingMessage(null);

    if (clientId == null || clientType === "seller" || !prefsPresent) {
      setMatches([]);
      setCount(0);
      setNextPage(null);
      setLoadState("idle");
      setLoadError(null);
      return undefined;
    }

    loadMatches({ mode: "initial", pageNum: 1 });

    return () => {
      requestGenerationRef.current += 1;
      abortActive();
      abortPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    clientType,
    prefsPresent,
    statusFilter,
    ordering,
    preferencesRevision,
  ]);

  // After preferences save (revision bump), bounded poll for async recompute.
  // Baseline is the count observed before this revision's refetch settles.
  const prevRevisionRef = useRef(0);
  useEffect(() => {
    if (!prefsPresent || clientType === "seller") return undefined;
    if (preferencesRevision === 0) return undefined;
    if (preferencesRevision === prevRevisionRef.current) return undefined;
    prevRevisionRef.current = preferencesRevision;
    const baseline = count;
    startBoundedPoll(baseline);
    return () => {
      abortPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesRevision, prefsPresent, clientType]);

  if (!client || clientType === "seller") {
    return null;
  }

  const summaryRows = buildPreferencesSummaryRows(
    client.preferences || null,
  );
  const summaryText = summaryRows
    .map((row) => `${row.label}: ${row.value}`)
    .join(" · ");

  const handleReload = () => {
    if (refreshing || loadState === "loading") return;
    loadMatches({ mode: "refresh", pageNum: 1 });
  };

  const handleLoadMore = () => {
    if (!nextPage || loadingMore || loadState === "loading") return;
    loadMatches({ mode: "load-more", pageNum: page + 1 });
  };

  const handleRecompute = async () => {
    if (!canEdit || recomputing || !prefsPresent) return;
    setRecomputing(true);
    setRecomputeError(null);
    const baseline = count;
    try {
      const result = await requestClientMatchesRecompute(clientId);
      if (result && result.enqueued === true) {
        toast.success("Перерахунок підборів запущено.");
        setPendingMessage("Оновлення підборів…");
        startBoundedPoll(baseline);
      } else {
        setRecomputeError(
          "Не вдалося поставити перерахунок у чергу. Спробуйте ще раз.",
        );
        setPendingMessage(null);
      }
    } catch (err) {
      setRecomputeError(messageFromError(err));
      setPendingMessage(null);
    } finally {
      setRecomputing(false);
    }
  };

  const handleMatchUpdated = (updatedMatch) => {
    setMatches((prev) =>
      prev.map((row) => (row.id === updatedMatch.id ? updatedMatch : row)),
    );
  };

  const requestEdit = () => {
    if (typeof onRequestEditPreferences === "function") {
      onRequestEditPreferences();
    }
  };

  // State 1 — no preferences
  if (!prefsPresent) {
    return (
      <section
        className="cd-section feature-client-detail__section--matches"
        aria-labelledby="client-detail-matches-heading"
      >
        <header className="cd-section__head">
          <Heading
            id="client-detail-matches-heading"
            level={2}
            variant="section"
            icon="fa-solid fa-house-circle-check"
          >
            Підбори
          </Heading>
        </header>
        <div className="feature-client-detail__matches-empty">
          <Text as="p" size="sm" tone="secondary">
            Щоб бачити підбори нерухомості, спочатку налаштуйте критерії
            пошуку в секції «Що шукає».
          </Text>
        </div>
      </section>
    );
  }

  return (
    <section
      className="cd-section feature-client-detail__section--matches"
      aria-labelledby="client-detail-matches-heading"
    >
      <header className="cd-section__head">
        <Heading
          id="client-detail-matches-heading"
          level={2}
          variant="section"
          icon="fa-solid fa-house-circle-check"
        >
          Підбори
          {loadState === "loaded" ? (
            <span className="feature-client-detail__matches-count">
              {" "}
              ({count})
            </span>
          ) : null}
        </Heading>
        <div className="feature-client-detail__section-actions feature-client-detail__matches-actions">
          {canEdit ? (
            <button
              type="button"
              className="cd-add-btn"
              onClick={requestEdit}
            >
              Змінити критерії
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="cd-add-btn"
              onClick={handleRecompute}
              disabled={recomputing}
              data-loading={recomputing ? "true" : undefined}
            >
              {recomputing ? "Запуск…" : "Перерахувати"}
            </button>
          ) : null}
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing || loadState === "loading"}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити підбори"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {summaryText ? (
        <p className="feature-client-detail__matches-criteria">
          <span className="feature-client-detail__matches-criteria-label">
            Критерії:
          </span>{" "}
          <span>{summaryText}</span>
        </p>
      ) : null}

      <div className="feature-client-detail__matches-filters">
        <label className="feature-client-detail__matches-filter">
          <span className="feature-client-detail__matches-filter-label">
            Статус
          </span>
          <select
            className="feature-client-detail__match-status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Фільтр статусу підборів"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="feature-client-detail__matches-filter">
          <span className="feature-client-detail__matches-filter-label">
            Сортування
          </span>
          <select
            className="feature-client-detail__match-status-select"
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            aria-label="Сортування підборів"
          >
            {ORDERING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {pendingMessage ? (
        <div
          className="feature-client-detail__matches-pending"
          role="status"
          aria-live="polite"
        >
          {pendingMessage}
        </div>
      ) : null}

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      {recomputeError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {recomputeError}
        </div>
      ) : null}

      {loadState === "loading" && matches.length === 0 ? (
        <div className="feature-client-detail__section-state">
          <Spinner size="md" label="Завантаження підборів" />
        </div>
      ) : loadState === "error" && matches.length === 0 ? (
        <div className="feature-client-detail__section-state">
          <Text as="p" size="sm" tone="danger">
            {loadError || "Не вдалося завантажити підбори."}
          </Text>
          <button
            type="button"
            className="feature-client-detail__action-btn"
            onClick={() => loadMatches({ mode: "initial", pageNum: 1 })}
          >
            Повторити
          </button>
        </div>
      ) : matches.length === 0 ? (
        <div className="feature-client-detail__matches-empty">
          <Text as="p" size="sm" tone="secondary">
            За поточними критеріями підборів немає. Спробуйте уточнити
            критерії пошуку або перерахувати.
          </Text>
          {canEdit ? (
            <button
              type="button"
              className="feature-client-detail__action-btn"
              onClick={requestEdit}
            >
              Змінити критерії
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="feature-client-detail__matches-grid">
            {matches.map((match) => (
              <ClientMatchCard
                key={match.id}
                match={match}
                clientId={clientId}
                canEdit={canEdit}
                onUpdated={handleMatchUpdated}
              />
            ))}
          </div>
          {loadMoreError ? (
            <div
              className="feature-client-detail__refresh-error"
              role="alert"
            >
              {loadMoreError}
            </div>
          ) : null}
          {nextPage ? (
            <div className="feature-client-detail__matches-load-more">
              <button
                type="button"
                className="feature-client-detail__action-btn feature-client-detail__action-btn--ghost"
                onClick={handleLoadMore}
                disabled={loadingMore}
                data-loading={loadingMore ? "true" : undefined}
              >
                {loadingMore ? (
                  <Spinner size="sm" label="Завантаження" />
                ) : (
                  "Показати ще"
                )}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

export default ClientMatchesSection;
